import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, UploadIcon, WifiIcon, ShieldAlertIcon, HomeIcon, ArrowRightIcon, ZapIcon, ServerIcon, UsersIcon } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
export function BusinessInternetPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-426">
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-427">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-428"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-429"></div>
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[100px] pointer-events-none" data-id="element-430"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-431">
          <div className="max-w-3xl" data-id="element-432">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-433">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-434">
                <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2" data-id="element-435"></span>
                Enterprise-Grade Connectivity
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-436">
                Business Internet Services
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-437">
                In today's fast-paced business environment, a reliable and
                high-speed internet connection is not just a luxury—it's a
                necessity. Whether you're running a small startup or a large
                enterprise, your business's productivity and success depend on
                the quality of your internet service. At Ambient Telecom, we
                offer customized business internet solutions tailored to meet
                your specific needs.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-438">
                Get a Free Analysis
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-439" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Download vs Upload */}
      <section className="py-20 bg-white" data-id="element-440">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-441">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-442">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-443">
              Download Speed Vs Upload Speed
            </h2>
            <p className="text-lg text-slate-600" data-id="element-444">
              When choosing a business internet plan, it's important to
              understand the difference between download and upload speeds to
              ensure your team has the bandwidth they need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-445">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 relative overflow-hidden" data-id="element-446">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-bl-full opacity-50 -z-10" data-id="element-447"></div>
              <div className="w-14 h-14 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center justify-center mb-6" data-id="element-448">
                <DownloadIcon className="w-7 h-7 text-blue-500" data-id="element-449" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-450">
                Download Speed
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-451">
                This measures how fast data can be downloaded from the internet
                to your devices. It's crucial for activities like streaming
                videos, downloading large files, and browsing the web
                efficiently without lag.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 relative overflow-hidden" data-id="element-452">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-100 rounded-bl-full opacity-50 -z-10" data-id="element-453"></div>
              <div className="w-14 h-14 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center justify-center mb-6" data-id="element-454">
                <UploadIcon className="w-7 h-7 text-green-500" data-id="element-455" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-456">
                Upload Speed
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-457">
                This measures how fast data can be sent from your devices to the
                internet. It's essential for video conferencing, uploading large
                files to the cloud, and running online applications smoothly.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Speed Tiers */}
      <section className="py-20 bg-slate-50" data-id="element-458">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-459">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-460">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-461">
              Find the Perfect Business Internet Speed
            </h2>
            <p className="text-lg text-slate-600" data-id="element-462">
              Ambient Telecom provides a reliable, scalable internet solution
              with up to 2 Gbps download and 880 Mbps upload speeds, enhancing
              connections through the largest gig-speed networks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-463">
            {[{
            speed: '150 Mbps – 300 Mbps',
            users: '1 to 3 users',
            desc: 'Handling basic tasks like downloading files and sending emails.',
            icon: UsersIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            speed: '500 Mbps – 800 Mbps',
            users: '3 to 5 users',
            desc: 'Perfect for small businesses needing to share files, stream videos, and process transactions efficiently.',
            icon: ZapIcon,
            color: 'text-primary',
            bg: 'bg-primary/10'
          }, {
            speed: '1.25 Gbps – 2 Gbps',
            users: '5 to 7+ users',
            desc: 'Suited for growing businesses, capable of supporting video conferencing and large data backups.',
            icon: ServerIcon,
            color: 'text-purple-500',
            bg: 'bg-purple-50'
          }].map((tier, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-464">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${tier.bg}`} data-id="element-465">
                  <tier.icon className={`w-6 h-6 ${tier.color}`} data-id="element-466" />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2" data-id="element-467">
                  {tier.speed}
                </h3>
                <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-468">
                  Great for {tier.users}
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-469">{tier.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Wifi & Backup */}
      <section className="py-20 bg-white" data-id="element-470">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-471">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-472">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-473">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-474">
                Advanced WiFi & Automatic Backup
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-475">
                We don't just provide the connection; we ensure it's secure,
                accessible, and always online when you need it most.
              </p>

              <div className="space-y-8" data-id="element-476">
                <div className="flex" data-id="element-477">
                  <div className="flex-shrink-0 mt-1" data-id="element-478">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-479">
                      <WifiIcon className="w-6 h-6 text-blue-600" data-id="element-480" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-481">
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-482">
                      Advanced WiFi Solutions
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-483">
                      With Business WiFi Pro, you get two networks—one private
                      for your team and one for guests. Advanced features like
                      customizable splash pages help drive business and track
                      trends.
                    </p>
                  </div>
                </div>

                <div className="flex" data-id="element-484">
                  <div className="flex-shrink-0 mt-1" data-id="element-485">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center" data-id="element-486">
                      <ShieldAlertIcon className="w-6 h-6 text-red-600" data-id="element-487" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-488">
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-489">
                      Automatic Backup
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-490">
                      Business Connection Pro ensures your business stays
                      connected during internet outages. With 4G LTE wireless
                      backup, you can maintain connectivity for up to 8 to 16
                      hours, even during a power outage.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-491">
              <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop" alt="Server room networking equipment" className="w-full h-auto object-cover" data-id="element-492" />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 to-transparent" data-id="element-493"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Work from Home */}
      <section className="py-20 bg-primary relative overflow-hidden" data-id="element-494">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)'
      }} data-id="element-495"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-496">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm" data-id="element-497">
            <HomeIcon className="w-8 h-8 text-white" data-id="element-498" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-499">
            Can Business Internet Be Used at Home?
          </h2>
          <p className="text-xl text-blue-100 leading-relaxed mb-8" data-id="element-500">
            Absolutely! Business internet plans are not just for office
            environments—they can also be used at home. Whether you're running a
            home office, managing a remote team, or simply need a more powerful
            connection for personal use, our business internet solutions provide
            the speed and reliability you need.
          </p>
          <p className="text-lg text-white font-medium" data-id="element-501">
            With higher performance than standard residential internet, our
            business plans are perfect for ensuring your work-from-home setup is
            just as efficient as being in the office.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection data-id="element-502" />

      {/* CTA */}
      <section className="py-20 bg-white border-t border-slate-100" data-id="element-503">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-504">
          <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-505">
            Ready to upgrade your connection?
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-id="element-506">
            Let our experts find the perfect internet solution for your specific
            business needs and location.
          </p>
          <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg" data-id="element-507">
            Get a Custom Quote
          </button>
        </div>
      </section>
    </div>;
}